<template>
    <div class="compass-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 class="h5 mb-0">Your Limit Orders</h3>
            <div class="d-flex gap-2 align-items-center">
                <select class="form-select form-select-sm" v-model="statusFilter" style="width: auto;">
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="filled">Filled</option>
                    <option value="expired">Expired</option>
                </select>
                <button class="compass-btn compass-btn--sm compass-btn--outline" @click="fetchOrders" :disabled="loading">
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                </button>
            </div>
        </div>

        <!-- Not Connected -->
        <div v-if="!isConnected" class="text-center py-4 text-muted">
            <i class="fas fa-wallet fa-2x mb-2 d-block"></i>
            <p class="small mb-0">Connect your wallet to view limit orders</p>
        </div>

        <!-- Loading -->
        <div v-else-if="loading && orders.length === 0" class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
        </div>

        <!-- Empty -->
        <div v-else-if="orders.length === 0" class="text-center py-4 text-muted">
            <i class="fas fa-crosshairs fa-2x mb-2 d-block"></i>
            <p class="small mb-0">No limit orders yet. Set a target price to get started.</p>
        </div>

        <!-- Orders -->
        <div v-else>
            <div v-for="order in orders" :key="order.orderKey" class="limit-order-card mb-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <span class="fw-bold">{{ order.inputSymbol || truncate(order.inputMint) }}</span>
                        <i class="fas fa-arrow-right mx-1 text-muted" style="font-size: 0.75rem;"></i>
                        <span class="fw-bold">{{ order.outputSymbol || truncate(order.outputMint) }}</span>
                    </div>
                    <span class="compass-badge" :class="statusBadgeClass(order.status)">
                        {{ order.status }}
                    </span>
                </div>

                <div class="row g-2 mb-2">
                    <div class="col-6">
                        <div class="text-muted small">Selling</div>
                        <div class="mono-num small">{{ formatLamports(order.makingAmount) }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-muted small">For</div>
                        <div class="mono-num small">{{ formatLamports(order.takingAmount) }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-muted small">Remaining</div>
                        <div class="mono-num small">{{ formatLamports(order.remainingMakingAmount) }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-muted small">Expires</div>
                        <div class="small">{{ order.expiredAt ? formatDate(order.expiredAt) : 'Never' }}</div>
                    </div>
                </div>

                <!-- Fill Progress -->
                <div class="mb-2" v-if="fillPercent(order) > 0">
                    <div class="progress-inline">
                        <div class="progress-bar" :style="{ width: fillPercent(order) + '%' }"></div>
                        <span>{{ fillPercent(order).toFixed(0) }}% filled</span>
                    </div>
                </div>

                <div class="d-flex justify-content-between align-items-center">
                    <span class="text-muted small">{{ formatDate(order.createdAt) }}</span>
                    <button
                        v-if="order.status === 'active'"
                        class="compass-btn compass-btn--sm compass-btn--outline text-danger"
                        @click="cancelOrder(order)"
                        :disabled="cancelling === order.orderKey"
                    >
                        <i class="fas" :class="cancelling === order.orderKey ? 'fa-spinner fa-spin' : 'fa-times'"></i>
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <div class="alert alert-danger small py-2 mt-2" v-if="error">{{ error }}</div>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';

export default {
    name: 'LimitOrderList',

    data() {
        return {
            orders: [],
            loading: false,
            cancelling: null,
            error: null,
            statusFilter: '',
        };
    },

    computed: {
        isConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        walletAddress() {
            return window.wallet?.publicKey?.value?.toBase58() || null;
        },
    },

    watch: {
        isConnected(val) {
            if (val) this.fetchOrders();
        },
        statusFilter() {
            this.fetchOrders();
        },
    },

    mounted() {
        if (this.isConnected) this.fetchOrders();
    },

    methods: {
        truncate(addr) {
            if (!addr) return '';
            return addr.slice(0, 4) + '...' + addr.slice(-4);
        },

        formatLamports(val) {
            if (!val) return '0';
            const num = Number(val);
            if (num >= 1e9) return (num / 1e9).toFixed(4);
            if (num >= 1e6) return (num / 1e6).toFixed(4);
            return num.toLocaleString();
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        },

        fillPercent(order) {
            const total = Number(order.makingAmount);
            const remaining = Number(order.remainingMakingAmount);
            if (!total || total === 0) return 0;
            return ((total - remaining) / total) * 100;
        },

        statusBadgeClass(status) {
            if (status === 'active') return 'compass-badge--purple';
            if (status === 'filled') return 'compass-badge--gradient';
            return 'compass-badge--outline';
        },

        async fetchOrders() {
            if (!this.walletAddress) return;
            this.loading = true;
            this.error = null;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const opts = this.statusFilter ? { status: this.statusFilter } : {};
                this.orders = await sdk.order.listLimit(this.walletAddress, opts);
            } catch (e) {
                this.error = e.message || 'Failed to load limit orders';
            } finally {
                this.loading = false;
            }
        },

        async cancelOrder(order) {
            this.cancelling = order.orderKey;
            this.error = null;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                await sdk.order.cancelLimit(order.orderKey, 'default');
                this.orders = this.orders.filter(o => o.orderKey !== order.orderKey);
            } catch (e) {
                this.error = e.message || 'Failed to cancel order';
            } finally {
                this.cancelling = null;
            }
        },
    },
};
</script>

<style scoped>
.limit-order-card {
    padding: 1rem;
    border-radius: var(--compass-radius-sm, 8px);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    background: var(--compass-gradient-subtle, rgba(139, 92, 246, 0.03));
}
</style>
